import { useParams } from 'react-router-dom'

import {
  LoadingContainer,
  ModelList,
  Form,
  Input,
  Bar,
  Panels,
  Select,
  Modal,
  StateCodeSelect,
  CountryCodeSelect,
} from 'peach/components'

import { Page } from 'core/components'
import {
  useInternalListContactsForBorrowerPaged,
  useInternalCreateContactForBorrower,
} from 'core/types'

const PersonContacts = () => {
  const { companyId, personId } = useParams<{
    companyId?: string
    personId?: string
  }>()

  const {
    data: phoneContacts,
    isLoading: isLoadingPhoneContacts,
    isFetchingNextPage: isFetchingNextPhoneContacts,
    error: errorPhoneContacts,
    fetchNextPage: fetchNextPhoneContacts,
    hasNextPage: hasNextPhoneContacts,
  } = useInternalListContactsForBorrowerPaged({
    companyId,
    personId,
    query: { limit: 50, contactType: 'phone' },
  })

  const {
    data: emailContacts,
    isLoading: isLoadingEmailContacts,
    isFetchingNextPage: isFetchingNextEmailContacts,
    error: errorEmailContacts,
    fetchNextPage: fetchNextEmailContacts,
    hasNextPage: hasNextEmailContacts,
  } = useInternalListContactsForBorrowerPaged({
    companyId,
    personId,
    query: { limit: 50, contactType: 'email' },
  })

  const {
    data: addressContacts,
    isLoading: isLoadingAddressContacts,
    isFetchingNextPage: isFetchingNextAddressContacts,
    error: errorAddressContacts,
    fetchNextPage: fetchNextAddressContacts,
    hasNextPage: hasNextAddressContacts,
  } = useInternalListContactsForBorrowerPaged({
    companyId,
    personId,
    query: { limit: 50, contactType: 'address' },
  })

  const {
    data: whatsAppContacts,
    isLoading: isLoadingWhatsAppContacts,
    isFetchingNextPage: isFetchingNextWhatsAppContacts,
    error: errorWhatsAppContacts,
    fetchNextPage: fetchNextWhatsAppContacts,
    hasNextPage: hasNextWhatsAppContacts,
  } = useInternalListContactsForBorrowerPaged({
    companyId,
    personId,
    query: { limit: 50, contactType: 'whatsApp' },
  })

  const { mutateAsync: createContact } = useInternalCreateContactForBorrower({
    companyId,
    personId,
  })

  const handleCreateContact = async (newContact) => {
    await createContact(newContact)
  }

  const createContactModal = (
    <Modal
      initialValue={{}}
      onSubmit={handleCreateContact}
      title='Create Contact'
      trigger
    >
      <Select
        formKey='status'
        options={['primary', 'secondary', 'additional', 'archived']}
      />
      <Select
        formKey='affiliation'
        options={[
          'self',
          'spouse',
          'parent',
          'guardian',
          'executor',
          'administrator',
          'otherRelative',
          'legal',
          'businessOwner',
          'businessExecutive',
          'businessEmployee',
        ]}
      />
      <Select formKey='label' options={['personal', 'home', 'work']} />
      <Select
        formKey='contactType'
        options={['phone', 'email', 'address', 'whatsApp', 'fbMessenger']}
      />
      <Form.If
        else={<Input formKey='value' />}
        if={{ contactType: 'address' }}
        then={
          <Form.Provider formKey='address' section>
            <Input formKey='addressLine1' />
            <Input formKey='addressLine2' />
            <Input formKey='city' />
            <Input formKey='countyOrRegion' />
            <StateCodeSelect formKey='state' nullable />
            <Input formKey='postalCode' />
            <CountryCodeSelect formKey='country' nullable />
          </Form.Provider>
        }
      />
    </Modal>
  )

  return (
    <Panels header={<Bar right={createContactModal} title='Contacts' />}>
      <LoadingContainer
        error={
          errorPhoneContacts ??
          errorEmailContacts ??
          errorAddressContacts ??
          errorWhatsAppContacts
        }
        isLoading={
          isLoadingPhoneContacts ||
          isLoadingEmailContacts ||
          isLoadingAddressContacts ||
          isLoadingWhatsAppContacts
        }
      >
        <Page variant='full'>
          <ModelList
            fetchNextPage={fetchNextPhoneContacts}
            hasNextPage={hasNextPhoneContacts}
            isFetchingNextPage={isFetchingNextPhoneContacts}
            json={phoneContacts}
            title='Phone Contacts'
          />
          <ModelList
            fetchNextPage={fetchNextEmailContacts}
            hasNextPage={hasNextEmailContacts}
            isFetchingNextPage={isFetchingNextEmailContacts}
            json={emailContacts}
            title='Email Contacts'
          />
          <ModelList
            fetchNextPage={fetchNextAddressContacts}
            hasNextPage={hasNextAddressContacts}
            isFetchingNextPage={isFetchingNextAddressContacts}
            json={addressContacts}
            title='Address Contacts'
          />
          <ModelList
            fetchNextPage={fetchNextWhatsAppContacts}
            hasNextPage={hasNextWhatsAppContacts}
            isFetchingNextPage={isFetchingNextWhatsAppContacts}
            json={whatsAppContacts}
            title='WhatsApp Contacts'
          />
        </Page>
      </LoadingContainer>
    </Panels>
  )
}

export default PersonContacts
